import React, { useState, useEffect } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, Users, AlertTriangle, Clock, FileText, 
  TrendingUp, Calendar, CheckCircle, XCircle, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EscritorioDashboard = () => {
  const { empresasDisponiveis, escritorio, empresaAtual, loading } = useEmpresa();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    totalEmpresas: 0,
    totalEmpregados: 0,
    inconsistenciasPendentes: 0,
    atestadosPendentes: 0,
    empresasComAlerta: []
  });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    carregarEstatisticas();
  }, [empresasDisponiveis]);

  const carregarEstatisticas = async () => {
    if (!empresasDisponiveis || empresasDisponiveis.length === 0) {
      setLoadingStats(false);
      return;
    }

    try {
      setLoadingStats(true);
      
      // Calcular estatísticas consolidadas
      let totalEmpregados = 0;
      let inconsistenciasPendentes = 0;
      let atestadosPendentes = 0;
      const empresasComAlerta = [];

      for (const empresa of empresasDisponiveis) {
        try {
          // Buscar empregados da empresa
          const empResponse = await api.get(`/empregados?empresa_id=${empresa.id}`);
          const empregadosEmpresa = empResponse.data?.length || 0;
          totalEmpregados += empregadosEmpresa;

          // Buscar inconsistências pendentes
          const incResponse = await api.get(`/inconsistencias-ponto?empresa_id=${empresa.id}&status_filtro=pendente`);
          const incPendentes = incResponse.data?.length || 0;
          inconsistenciasPendentes += incPendentes;

          // Buscar atestados pendentes
          const atestResponse = await api.get(`/atestados?empresa_id=${empresa.id}&status_filtro=pendente`);
          const atestPendentes = atestResponse.data?.length || 0;
          atestadosPendentes += atestPendentes;

          // Se tem alertas, adicionar à lista
          if (incPendentes > 0 || atestPendentes > 0) {
            empresasComAlerta.push({
              ...empresa,
              inconsistencias: incPendentes,
              atestados: atestPendentes
            });
          }
        } catch (err) {
          // Silenciar erros de empresas individuais
          console.error(`Erro ao carregar dados de ${empresa.razao_social}:`, err);
        }
      }

      setStats({
        totalEmpresas: empresasDisponiveis.length,
        totalEmpregados,
        inconsistenciasPendentes,
        atestadosPendentes,
        empresasComAlerta: empresasComAlerta.sort((a, b) => 
          (b.inconsistencias + b.atestados) - (a.inconsistencias + a.atestados)
        ).slice(0, 5)
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escritorio-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard Consolidado</h1>
          <p className="text-slate-600 mt-1">
            Visão geral de todas as empresas gerenciadas
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          {escritorio?.nome_fantasia || escritorio?.razao_social || 'Escritório'}
        </Badge>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/escritorio/empresas')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Empresas</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {loadingStats ? '-' : stats.totalEmpresas}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Empregados</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {loadingStats ? '-' : stats.totalEmpregados}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/escritorio/alertas')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Inconsistências</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {loadingStats ? '-' : stats.inconsistenciasPendentes}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stats.inconsistenciasPendentes > 0 ? 'bg-red-100' : 'bg-slate-100'
              }`}>
                <AlertTriangle className={`w-6 h-6 ${
                  stats.inconsistenciasPendentes > 0 ? 'text-red-600' : 'text-slate-400'
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Atestados</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {loadingStats ? '-' : stats.atestadosPendentes}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stats.atestadosPendentes > 0 ? 'bg-amber-100' : 'bg-slate-100'
              }`}>
                <FileText className={`w-6 h-6 ${
                  stats.atestadosPendentes > 0 ? 'text-amber-600' : 'text-slate-400'
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Empresas com Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Empresas com Pendências
            </CardTitle>
            <CardDescription>
              Empresas que requerem atenção imediata
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : stats.empresasComAlerta.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
                <p>Nenhuma pendência encontrada</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.empresasComAlerta.map((empresa) => (
                  <div 
                    key={empresa.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/escritorio/empresas?empresa=${empresa.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {empresa.nome_fantasia || empresa.razao_social}
                        </p>
                        <p className="text-xs text-slate-500">{empresa.cnpj}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {empresa.inconsistencias > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {empresa.inconsistencias} inconsist.
                        </Badge>
                      )}
                      {empresa.atestados > 0 && (
                        <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                          {empresa.atestados} atest.
                        </Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ações Rápidas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Ações Rápidas
            </CardTitle>
            <CardDescription>
              Acesso rápido às funcionalidades mais usadas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => navigate('/escritorio/empresas')}
              >
                <Building2 className="w-5 h-5 text-blue-600" />
                <span className="text-sm">Ver Empresas</span>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => navigate('/escritorio/alertas')}
              >
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-sm">Ver Alertas</span>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => navigate('/escritorio/relatorios')}
              >
                <FileText className="w-5 h-5 text-green-600" />
                <span className="text-sm">Relatórios</span>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => navigate('/escritorio/configuracoes')}
              >
                <Clock className="w-5 h-5 text-purple-600" />
                <span className="text-sm">Configurações</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Todas as Empresas */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Todas as Empresas</CardTitle>
            <CardDescription>
              {stats.totalEmpresas} empresa{stats.totalEmpresas !== 1 ? 's' : ''} vinculada{stats.totalEmpresas !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/escritorio/empresas')}>
            Ver todas
          </Button>
        </CardHeader>
        <CardContent>
          {loadingStats || loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : empresasDisponiveis.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-500">
              <Building2 className="w-10 h-10 mb-2 opacity-50" />
              <p>Nenhuma empresa vinculada</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {empresasDisponiveis.slice(0, 6).map((empresa) => (
                <div 
                  key={empresa.id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/escritorio/empresas?empresa=${empresa.id}`)}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">
                      {empresa.nome_fantasia || empresa.razao_social}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{empresa.cnpj}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EscritorioDashboard;
